import React, { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// MUI components
import {
  Box,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";

// MUI icons
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SendSharpIcon from "@mui/icons-material/SendSharp";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

// Components and functions from project
import CommonAccordion from "../../custom-components/CommonAccordion";
import { flattenArray, flattenObject } from "../../../utils/dataFlatteners";
import { refinedUserstory } from "../../../services/webServices";
import { refineUserStoryWithRAG } from "../../../services/webServices"; // <-- Import the new service added
import CommonDownload from "../../custom-components/CommonDownload";
import CommonEvaluationTable from "../../custom-components/CommonEvaluationTable";
import {
  UserStoryFlowDataContext,
  UserStoryLoaderStateContext,
} from "../../main-pages/UserStoryMainPage";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import MessagePopup from "../../popup-components/MessagePopup";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI";

const EvaluateSummary = () => {
  const [evaluationSummaryDat, setEvaluationSummaryData] = useState([
    {
      Parameter: "Independent",
      Rating: "3",
      Explanation: "Some Explanation",
      Recommendation: "Some Recommendations",
    },
    {
      Parameter: "Negotiable",
      Rating: "3.5",
      Explanation: "Some Explanation",
      Recommendation: "Some Recommendations",
    },
    {
      Parameter: "Valuable",
      Rating: "2",
      Explanation: "Some Explanation",
      Recommendation: "Some Recommendations",
    },
    {
      Parameter: "Estimable",
      Rating: "5",
      Explanation: "Some Explanation",
      Recommendation: "Some Recommendations",
    },
    {
      Parameter: "Small",
      Rating: "4.8",
      Explanation: "Some Explanation",
      Recommendation: "Some Recommendations",
    },
    {
      Parameter: "Testable",
      Rating: "3",
      Explanation: "Some Explanation",
      Recommendation: "Some Recommendations",
    },
  ]);

  const [openDownload, setOpenDownload] = useState(false);
  const { setLoadingState } = useContext(LoadingModalContext);
  const { setUserStoryLoaderState } = useContext(UserStoryLoaderStateContext);
  const { setRefinedUserStory, setUseCase } = useContext(
    UserStoryFlowDataContext
  );

  const location = useLocation();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [activeThumb, setActiveThumb] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [refineOption, setRefineOption] = useState(null);

  const { refinedUserStory } = useContext(UserStoryFlowDataContext);
  const evaluationSummaryData = location.state["evaluationSummary"];
  const userStory = location.state["userStory"];
  const isRAGavaiable =
    location.state["isRAGavaiable"] !== undefined
      ? location.state["isRAGavaiable"]
      : false;

  const defaultEvalutaionSummaryExpanded =
    evaluationSummaryData.length > 0 &&
    Object.keys(refinedUserStory).length === 0;

  const formattedEvaluationSummary = [];
  evaluationSummaryData.map((evaluationSummary) => {
    const flattenEvaluationSummary = {};
    Object.entries(evaluationSummary).map(([key, value]) => {
      if (Array.isArray(value)) {
        return (flattenEvaluationSummary[key] = flattenArray(value));
      } else if (typeof value === "object" && !!value) {
        return (flattenEvaluationSummary[key] = flattenObject(value));
      } else {
        return (flattenEvaluationSummary[key] = value);
      }
    });
    return formattedEvaluationSummary.push(flattenEvaluationSummary);
  });

  const handleThumbClick = (thumbType) => {
    setActiveThumb((prev) => (prev === thumbType ? null : thumbType));
  };

  const handleCopyClick = () => {
    navigator.clipboard.writeText("Content to be copied");
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleDownload = () => {
    setOpenDownload(true);
  };

  const handleRefinedUserStory = async () => {
    //setLoading(true);
    setRefinedUserStory({});
    setUseCase({});
    setUserStoryLoaderState("Refined User Story");
    let progressHandler;
    try {
      progressHandler = handleProgressBar({ setLoadingState, totalDurationMs: 16000 });
      progressHandler.start("Refining User story");
      const requestData = {
        EvaluationSummary: evaluationSummaryData,
        UserStory: userStory,
      };
      const refinedUserStory = await refinedUserstory(requestData);
      await progressHandler.finishAndClose();

      navigate("/dashboard/userstory/output", {
        state: {
          ...location.state,
          refinedUserStory: refinedUserStory.data["refinedUserStory"],
          useCase: {},
          testcases: [],
        },
        replace: true,
      });
    } catch (error) {
      const errorMsg =
        error?.response?.data?.Error || "An error occurred. Please try again.";
      setPopupMessage(errorMsg);
      setPopupOpen(true);
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      if (progressHandler) {
        progressHandler.stop();
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const handleRefineWithRAG = async () => {
    setLoadingState({ open: true, message: "Refining User Story with RAG", progress: null });
    setRefinedUserStory({});
    setUseCase({});
    setUserStoryLoaderState("Refined User Story");
    try {
      const requestData = {
        EvaluationSummary: evaluationSummaryData,
        UserStory: userStory,
      };
      const refinedUserStory = await refineUserStoryWithRAG(requestData);
      navigate("/dashboard/userstory/output", {
        state: {
          ...location.state,
          refinedUserStory: refinedUserStory.data["refinedUserStory"],
          useCase: {},
          testcases: [],
        },
        replace: true,
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const handleRefine = () => {
    if (refineOption === "userStory") handleRefinedUserStory();
    else if (refineOption === "rag") handleRefineWithRAG();
  };

  const refineControls = (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"

      sx={{ width: "100%" }}
    >
      <RadioGroup
        row
        value={refineOption}
        onChange={(e) => setRefineOption(e.target.value)}
        sx={{ display: "flex", gap: 2 }}
      >
        <FormControlLabel
          value="userStory"
          control={
            <Radio
              sx={{
                "& .MuiSvgIcon-root": {
                  fontSize: 14,
                },
              }}
            />
          }
          label="Refine User Story"
          sx={{
            "& .MuiFormControlLabel-label": {
              fontSize: "14px",
            },
          }}
        />

        <FormControlLabel
          value="rag"
          control={
            <Radio
              disabled={!isRAGavaiable}
              sx={{
                "& .MuiSvgIcon-root": {
                  fontSize: 14,
                },
              }}
            />
          }
          label="Refine With RAG"
          sx={{
            "& .MuiFormControlLabel-label": {
              fontSize: "14px",
            },
          }}
        />
      </RadioGroup>

      <Button
        variant="contained"
        onClick={handleRefine}
        disabled={!refineOption}
        sx={{
          textTransform: "none",
          fontSize: 14,
          backgroundColor: "#49A3FF",
          whiteSpace: "nowrap",
        }}
      >
        Refine
      </Button>
    </Box>
  );

  const tableDataConverter = (data) => {
    const headers = ["Parameter", ...data.map((item) => item.Parameter)];
    const rowLabels = Object.keys(data[0]).filter((key) => key !== "Parameter");
    const values = rowLabels.map((rowKey) => [
      rowKey,
      ...data.map((item) => item[rowKey]),
    ]);
    return { headers, values };
  };

  const evaluationSummaryTable = (
    <Box sx={{ height: "100%" }}>
      <Box sx={{ maxHeight: "60vh", height: "100%" }}>
        <CommonEvaluationTable
          {...tableDataConverter(formattedEvaluationSummary)}
          edittable={true}
          tableData={formattedEvaluationSummary}
          setTableData={setEvaluationSummaryData}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginTop: 4,
          marginBottom: 2,
        }}
      >
        <Tooltip title="Like" arrow>
          <Box
            onClick={() => handleThumbClick("like")}
            sx={{
              cursor: "pointer",
              marginRight: 2,
              "&:hover": { color: "#49A3FF" },
            }}
          >
            {activeThumb === "like" ? (
              <ThumbUpIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ThumbUpOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>
        <Tooltip title="Dislike" arrow>
          <Box
            onClick={() => handleThumbClick("dislike")}
            sx={{
              cursor: "pointer",
              marginRight: 2,
              "&:hover": { color: "#FF4842" },
            }}
          >
            {activeThumb === "dislike" ? (
              <ThumbDownIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ThumbDownOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>
        <Tooltip title={isCopied ? "Copied!" : "Copy"} arrow>
          <Box
            onClick={handleCopyClick}
            sx={{
              cursor: "pointer",
              "&:hover": { color: "#49A3FF" },
            }}
          >
            {isCopied ? (
              <ContentCopyIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ContentCopyOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        maxHeight: "100vh",
        // width: "900px",
      }}
    >
      <CommonAccordion
        summary={"Evaluation Summary"}
        details={evaluationSummaryTable}
        type={"output"}
        buttons={[
          {
            label: "Download",
            onClick: handleDownload,
            variant: "text",
            startIcon: <FileDownloadOutlinedIcon />,
            sx: { textTransform: "none" },
          },
          // {
          //   label: "Refine User Story",
          //   onClick: handleRefinedUserStory,
          //   variant: "contained",
          //   startIcon: <GenerateButton />,
          //   sx: { float: "right", textTransform: "none" },
          // },
          // {
          //   label: "Refine With RAG",
          //   onClick: handleRefineWithRAG,
          //   variant: "contained",
          //   startIcon: <GenerateButton />,
          //   sx: { float: "right", textTransform: "none" },
          //   disabled: !isRAGavaiable,
          // },
          {
            label: refineControls,
            // onClick: () => {}, // required placeholder
            variant: "text",
            // sx: { padding: 0, minWidth: 0 },
            sx: {
              textTransform: "none",
              cursor: "default",
              "&.Mui-disabled": {
                color: "black", // Prevents graying out
                opacity: 1, // Restores normal opacity
              },
              float: "right"
              // "&:hover": {
              //   backgroundColor: "transparent",
              // },
              // "&:active": {
              //   backgroundColor: "transparent",
              // },
            },
          },
        ]}
        fullScreenMode={true}
        defaultExpanded={defaultEvalutaionSummaryExpanded}
      />
      <CommonDownload
        open={openDownload}
        onClose={() => setOpenDownload(false)}
        serviceResponse={[
          {
            heading: "User Story",
            type: "text",
            data:
              typeof userStory === "string" ? userStory : userStory["Title"],
          },
          {
            heading: "Evaluation Summary",
            type: "table",
            data: { ...tableDataConverter(formattedEvaluationSummary) },
          },
        ]}
        serviceName="Evaluation Summary Report"
        wordData={[
          {
            heading: "User Story",
            type: "text",
            data:
              typeof userStory === "string" ? userStory : userStory["Title"],
          },
          {
            heading: "Evaluation Summary",
            type: "table",
            data: { ...tableDataConverter(formattedEvaluationSummary) },
          },
        ]}
        documents={["docx", "pdf"]}
      />
      <MessagePopup
        modalOpen={popupOpen}
        okayHandler={() => setPopupOpen(false)}
        message={popupMessage}
      />
    </Box>
  );
};

export default EvaluateSummary;
